/**
 * PageEditorSidebar — Right-side floating sidebar with drill-down navigation.
 * Matches TemplateSettingsPanel's design: 280px, rounded-[20px], frosted glass.
 * Desktop: inline panel. Mobile: Sheet slide-in from right.
 */

import { useState, useCallback, useImperativeHandle, forwardRef } from 'react'
import { Paintbrush } from 'lucide-react'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { DrillCard } from './components/DrillCard'
import { SectionList } from './SectionList'
import { SectionConfigPanel } from './SectionConfigPanel'
import { WidgetLibraryPanel } from './WidgetLibraryPanel'
import { GlobalStylesPanel } from './GlobalStylesPanel'
import { BlockInspector } from './BlockInspector'
import { EmptyPageState } from './EmptyPageState'
import { AddSectionButton } from './AddSectionButton'
import type { EditorSection } from './usePageSections'
import type { GlobalStyles } from './useGlobalStyles'
import type { PreviewDevice } from './PagePreviewFrame'
import type { SelectedBlockInfo } from './useBlockSelection'
import type { SectionLookup, SectionEnvelope } from '@zaviago/storefront-config'

export type SidebarPage = 'main' | 'section-config' | 'library' | 'theme' | 'block-inspector'

export interface PageEditorSidebarRef {
  navigateTo: (page: SidebarPage) => void
}

interface PageEditorSidebarProps {
  // Section management
  sections: EditorSection[]
  configLoading: boolean
  selectedSectionId: string | null
  onSectionSelect: (editorId: string) => void
  onReorder: (activeId: string, overId: string) => void
  onReorderChildren: (parentEditorId: string, activeId: string, overId: string) => void
  onReorderBlocks: (editorId: string, groupId: string, activeBlockId: string, overBlockId: string) => void
  onDelete: (editorId: string) => void
  findByEditorId: (editorId: string) => SectionLookup<EditorSection> | null
  onAddChild: (parentEditorId: string, depth: number) => void
  onMoveToContainer: (editorId: string, containerId: string) => void
  onMoveToRoot: (editorId: string) => void
  onDuplicate: (editorId: string) => void
  onSelectFirstBlock: (editorId: string) => void

  // Section config
  selectedSection: EditorSection | null
  onSectionUpdate: (patch: Partial<SectionEnvelope>) => void
  onConfigBack: () => void
  activeBreakpoint: PreviewDevice
  onBreakpointEdit: (bp: 'desktop' | 'tablet' | 'mobile') => void
  onBlockSelect: (info: SelectedBlockInfo) => void
  onResetToDefault: (sectionPath: string) => void

  // Widget library
  onAddWidget: (type: string) => void
  addTarget: { parentEditorId: string; depth: number } | null
  onClearAddTarget: () => void

  // Theme
  globalStyles: GlobalStyles
  onBrandColorChange: (color: string | undefined) => void
  onFontFamilyChange: (font: string) => void
  onButtonStyleChange: (style: string) => void
  onSectionSpacingChange: (spacing: string) => void
  onPageMaxWidthChange: (width: number | undefined) => void

  // Block inspector
  blockSelection: {
    selectedBlock: SelectedBlockInfo | null
    handleBlockUpdate: (blockId: string, content: any) => void
    handleBlockWidthChange: (blockId: string, widthPct: number) => void
    deselectBlock: () => void
  }

  // Mobile sheet
  mobileOpen?: boolean
  onMobileOpenChange?: (open: boolean) => void
}

export const PageEditorSidebar = forwardRef<PageEditorSidebarRef, PageEditorSidebarProps>(
  function PageEditorSidebar(props, ref) {
    const {
      sections, configLoading, selectedSectionId, onSectionSelect,
      onReorder, onReorderChildren, onReorderBlocks, onDelete, findByEditorId,
      onAddChild, onMoveToContainer, onMoveToRoot, onDuplicate, onSelectFirstBlock,
      selectedSection, onSectionUpdate, onConfigBack, activeBreakpoint, onBreakpointEdit,
      onBlockSelect, onResetToDefault,
      onAddWidget, addTarget, onClearAddTarget,
      globalStyles, onBrandColorChange, onFontFamilyChange, onButtonStyleChange,
      onSectionSpacingChange, onPageMaxWidthChange,
      blockSelection,
      mobileOpen, onMobileOpenChange,
    } = props

    const [sidebarPage, setSidebarPage] = useState<SidebarPage>('main')
    const isDesktop = useMediaQuery('(min-width: 768px)')

    const navigateTo = useCallback((page: SidebarPage) => {
      setSidebarPage(page)
    }, [])

    useImperativeHandle(ref, () => ({ navigateTo }), [navigateTo])

    const handleSectionSelect = useCallback((editorId: string) => {
      onSectionSelect(editorId)
      setSidebarPage('section-config')
    }, [onSectionSelect])

    const handleConfigBack = useCallback(() => {
      onConfigBack()
      setSidebarPage('main')
    }, [onConfigBack])

    const handleOpenLibrary = useCallback(() => {
      onClearAddTarget()
      setSidebarPage('library')
    }, [onClearAddTarget])

    const handleOpenLibraryForContainer = useCallback((parentEditorId: string, depth: number) => {
      onAddChild(parentEditorId, depth)
      setSidebarPage('library')
    }, [onAddChild])

    const handleLibraryBack = useCallback(() => {
      onClearAddTarget()
      setSidebarPage('main')
    }, [onClearAddTarget])

    const handleAddWidget = useCallback((type: string) => {
      onAddWidget(type)
      setSidebarPage('section-config')
    }, [onAddWidget])

    const handleBlockBack = useCallback(() => {
      blockSelection.deselectBlock()
      if (selectedSectionId) {
        setSidebarPage('section-config')
      } else {
        setSidebarPage('main')
      }
    }, [blockSelection, selectedSectionId])

    // Render the current drill-down page
    const content = (() => {
      switch (sidebarPage) {
        case 'section-config':
          if (blockSelection.selectedBlock) {
            return (
              <BlockInspector
                block={blockSelection.selectedBlock}
                onUpdate={blockSelection.handleBlockUpdate}
                onWidthChange={blockSelection.handleBlockWidthChange}
                onBack={handleBlockBack}
              />
            )
          }
          return selectedSection ? (
            <SectionConfigPanel
              section={selectedSection}
              onUpdate={onSectionUpdate}
              onBack={handleConfigBack}
              activeBreakpoint={activeBreakpoint}
              onBreakpointEdit={onBreakpointEdit}
              sections={sections}
              onSelectSection={handleSectionSelect}
              onBlockSelect={onBlockSelect}
              onResetToDefault={onResetToDefault}
            />
          ) : null

        case 'library':
          return (
            <WidgetLibraryPanel
              onAdd={handleAddWidget}
              onBack={handleLibraryBack}
              addTarget={addTarget}
            />
          )

        case 'theme':
          return (
            <GlobalStylesPanel
              styles={globalStyles}
              onBrandColorChange={onBrandColorChange}
              onFontFamilyChange={onFontFamilyChange}
              onButtonStyleChange={onButtonStyleChange}
              onSectionSpacingChange={onSectionSpacingChange}
              onPageMaxWidthChange={onPageMaxWidthChange}
              onBack={() => setSidebarPage('main')}
            />
          )

        case 'block-inspector':
          return blockSelection.selectedBlock ? (
            <BlockInspector
              block={blockSelection.selectedBlock}
              onUpdate={blockSelection.handleBlockUpdate}
              onWidthChange={blockSelection.handleBlockWidthChange}
              onBack={handleBlockBack}
            />
          ) : null

        default: // 'main'
          return (
            <div className="p-4 space-y-3">
              {configLoading ? (
                <div className="flex flex-col gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-12 animate-pulse rounded-sm bg-gray-100" />
                  ))}
                </div>
              ) : sections.length === 0 ? (
                <EmptyPageState onAddSection={handleOpenLibrary} />
              ) : (
                <>
                  <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Sections
                  </h2>
                  <SectionList
                    sections={sections}
                    onReorder={onReorder}
                    onReorderChildren={onReorderChildren}
                    onReorderBlocks={onReorderBlocks}
                    onDelete={onDelete}
                    selectedId={selectedSectionId}
                    onSelect={handleSectionSelect}
                    findByEditorId={findByEditorId}
                    onAddChild={handleOpenLibraryForContainer}
                    onMoveToContainer={onMoveToContainer}
                    onMoveToRoot={onMoveToRoot}
                    onDuplicate={onDuplicate}
                    onSelectFirstBlock={onSelectFirstBlock}
                  />
                  <AddSectionButton onClick={handleOpenLibrary} />
                </>
              )}

              <div className="space-y-2 pt-2">
                <DrillCard
                  label="Theme"
                  value={globalStyles.brandColor || 'Default'}
                  icon={<Paintbrush className="h-4 w-4" />}
                  preview={
                    globalStyles.brandColor ? (
                      <div
                        className="h-5 w-5 rounded-full border border-border"
                        style={{ backgroundColor: globalStyles.brandColor }}
                      />
                    ) : undefined
                  }
                  onClick={() => setSidebarPage('theme')}
                />
              </div>
            </div>
          )
      }
    })()

    if (!isDesktop) {
      return (
        <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
          <SheetContent side="right" className="w-[300px] p-0 [&>button]:hidden">
            <SheetTitle className="sr-only">Page Editor</SheetTitle>
            <div className="h-full overflow-y-auto [scrollbar-width:none]">
              {content}
            </div>
          </SheetContent>
        </Sheet>
      )
    }

    return (
      <div className="w-[280px] shrink-0 rounded-[20px] backdrop-blur-xl bg-white/70 border border-border/40 shadow-lg overflow-y-auto [scrollbar-width:none]">
        {content}
      </div>
    )
  },
)
